import { BannedTailNumbersForm } from '@lp/features/admin/forms/BannedTailNumbersForm';
import {
  BANNED_TAIL_NUMBERS_DEFAULT_VALUES,
  bannedTailNumbersFormSchema,
} from '@lp/features/admin/forms/BannedTailNumbersForm/config';
import { AdminPageHeader, Form, AdminFormAction, DeleteModal } from '@ui';
import { useNavigate } from 'react-router-dom';
import { useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '@lp/core/auth';
import { useLoaderData } from '@/hooks/useLoaderData';
import { getChangedValues } from '@/utils/helpers';
import { retrieveUnifiedError } from '@/api/utils';
import { useRemoveBannedTailNumberMutation, useUpdateBannedTailNumbersMutation } from '@/queries/banned-tail-numbers';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { BannedTailNumber, BannedTailNumberFormType } from '@/api/apis/resources';
import { AdminPermissions } from '@/permissions';

export const BannedTailNumbersEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<BannedTailNumber>();
  const { mutateAsync } = useUpdateBannedTailNumbersMutation();
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.ProvidersConfigurationCreate);
  const hasEditPermissions = checkPermission(AdminPermissions.ProvidersConfigurationUpdate);

  const defaultValues = useMemo<BannedTailNumberFormType>(() => {
    return {
      ...BANNED_TAIL_NUMBERS_DEFAULT_VALUES,
      tailNumber: data.tailNumber,
      logisticProviderId: data.logisticProviderId || '',
      logisticProviderName: data.logisticProvider?.name || '',
      comment: data.comment || '',
    };
  }, [data]);

  const handleBackToList = () => {
    navigate('/admin/configuration/banned_tail_numbers');
  };

  const handleSubmit = async (values: BannedTailNumberFormType) => {
    try {
      const updateBody = {
        tailNumber: values.tailNumber,
        logisticProviderId: values.logisticProviderId || null,
        comment: values.comment,
      };

      const changedValues = getChangedValues<Partial<BannedTailNumberFormType>>(updateBody, defaultValues);

      if (Object.keys(changedValues).length) {
        await mutateAsync({
          id: data.id,
          ...changedValues,
        });
      }

      toast.success(`Banned Tail Number updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useRemoveBannedTailNumberMutation,
    successMessage: 'Banned Tail Number deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  const handlBannedTailNumberDelete = () => {
    onModalOpen(data.id);
  };

  return (
    <>
      <Form<BannedTailNumberFormType>
        defaultValues={defaultValues}
        schema={bannedTailNumbersFormSchema}
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-9">
          <AdminPageHeader
            title="Edit Tail Number Assignment"
            backToListLink="/admin/configuration/banned_tail_numbers"
          >
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handlBannedTailNumberDelete}
              hasDeletePermissions={hasCreatePermissions}
              hasEditPermissions={hasCreatePermissions || hasEditPermissions}
            />
          </AdminPageHeader>
          <BannedTailNumbersForm isReadOnly={!hasCreatePermissions && !hasEditPermissions} />
        </div>
      </Form>
      <DeleteModal
        title="Delete Banned Tail Number?"
        description="Are you sure you want to delete the Banned Tail Number?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
